// Publicación al SII (0108, Rodrigo 2026-10-24; como el «Motor SII» de BotiOS). Dos interruptores:
// FACTURAS (facturas, notas de crédito y de débito) y BOLETAS. Apagado, se sigue vendiendo y
// timbrando, pero lo emitido de ese grupo queda EN COLA sin salir al SII: se puede revisar y corregir
// (anular antes del envío, desde Correcciones de la nota) y, al encender, se publica en el siguiente
// ciclo. Apagar pide motivo; cada cambio queda con quién y cuándo.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { ImprimirVale } from '../ventas/ValeVenta';
import { AnularVentaDuplicada, PasarAVale } from './EstadoDte';

type Grupo = 'FACTURAS' | 'BOLETAS';
interface EstadoGrupo {
  grupo: Grupo;
  tipos: number[];
  activa: boolean;
  motivo: string | null;
  cambiadoEn: string | null;
  cambiadoPor: string | null;
  enCola: number;
  montoCola: string;
  masAntiguo: string | null;
}
interface EnCola {
  id: string;
  tipo_dte: number;
  folio: number | null;
  fecha_emision: string;
  receptor_rut: string;
  receptor: string | null;
  monto_total: string;
  creado_en: string;
  origen_id: string;
  /** La venta detrás del documento: de dónde salió y cómo se paga. */
  venta: {
    documentacionId: string;
    documentacionEstado: string;
    notaId: string;
    notaNumero: string;
    canal: string;
    entrega: string;
    origen: 'LOCAL' | 'DESPACHO';
    condicionPago: string;
    credito: boolean;
  } | null;
}

const CONDICION: Record<string, string> = {
  contado: 'Contado',
  transferencia: 'Transferencia',
  cheque_al_dia: 'Cheque al día',
  cheque_a_plazo: 'Cheque a plazo',
  credito: 'Crédito',
};
const CANAL: Record<string, string> = {
  POS: 'Punto de venta',
  MESON: 'Mesón',
  VENDEDOR: 'Vendedor',
  CALL_CENTER: 'Call center',
  RUTA: 'Ruta',
  B2B: 'B2B',
};

const NOMBRE: Record<Grupo, string> = {
  FACTURAS: 'Facturas y notas de crédito',
  BOLETAS: 'Boletas',
};
const DETALLE: Record<Grupo, string> = {
  FACTURAS: 'Factura (33), factura exenta (34), nota de débito (56) y nota de crédito (61)',
  BOLETAS: 'Boleta (39) y boleta exenta (41)',
};
const TIPO: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  39: 'Boleta',
  41: 'Boleta exenta',
  56: 'Nota de débito',
  61: 'Nota de crédito',
};

/** Minutos desde un instante (para avisar lo que lleva mucho en cola). */
const minutosDesde = (iso: string) =>
  Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));

export function PublicacionDte({
  alAbrirNota,
}: {
  alAbrirNota: (notaId: string, titulo: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const administra = puede('dte.publicacion.administrar');
  const estado = useQuery({
    queryKey: ['emp', empresaId, 'dte-publicacion'],
    queryFn: () => api.get<EstadoGrupo[]>('/dte/publicacion', { query: { empresaId } }),
    refetchInterval: 15_000,
  });
  const [apagar, setApagar] = useState<Grupo | null>(null);
  const [cola, setCola] = useState<Grupo | null>(null);
  const qc = useQueryClient();
  const cambiar = useMutation({
    mutationFn: (b: { grupo: Grupo; activa: boolean; motivo?: string }) =>
      api.post('/dte/publicacion', { empresaId, ...b }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emp', empresaId, 'dte-publicacion'] }),
  });
  return (
    <div className="pantalla consulta publicacion-dte" data-pantalla="publicacion-dte">
      <p className="apagado">
        Apagada, se sigue vendiendo y emitiendo, pero lo de ese grupo <b>no sale al SII</b>: queda
        en cola para revisarlo y corregirlo: «Abrir para corregir» lleva a la nota, donde una boleta
        se puede <b>pasar a vale</b> (con el mismo pago y reutilizando su folio) o anularse antes
        del envío. Al encenderla se publica todo lo que esperaba.
      </p>
      {estado.isError && <PanelError error={estado.error} />}
      {cambiar.isError && <PanelError error={cambiar.error} />}
      <div className="publicacion-grupos">
        {(estado.data ?? []).map((g) => {
          const minutos = g.masAntiguo ? minutosDesde(g.masAntiguo) : 0;
          return (
            <fieldset
              key={g.grupo}
              className={`recuadro-publicacion ${g.activa ? 'encendida' : 'apagada'}`}
              data-publicacion={g.grupo}
              data-activa={g.activa}
            >
              <legend>{NOMBRE[g.grupo]}</legend>
              <p className="apagado">{DETALLE[g.grupo]}</p>
              <div className="publicacion-estado">
                <span className={`insignia grande tono-${g.activa ? 'ok' : 'malo'}`}>
                  {g.activa ? 'PUBLICANDO AL SII' : 'PUBLICACIÓN APAGADA'}
                </span>
                {administra &&
                  (g.activa ? (
                    <button type="button" className="peligro" onClick={() => setApagar(g.grupo)}>
                      Apagar publicación
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="primario"
                      disabled={cambiar.isPending}
                      onClick={() => cambiar.mutate({ grupo: g.grupo, activa: true })}
                    >
                      Encender y publicar
                    </button>
                  ))}
              </div>
              {!g.activa && (
                <p>
                  <b>Motivo:</b> {g.motivo ?? '—'}
                </p>
              )}
              {g.cambiadoEn && (
                <p className="apagado">
                  {g.activa ? 'Encendida' : 'Apagada'} por {g.cambiadoPor ?? '—'} el{' '}
                  {fechaHora(g.cambiadoEn)}
                </p>
              )}
              <p>
                En cola sin enviar: <b>{g.enCola}</b> {g.enCola === 1 ? 'documento' : 'documentos'}
                {g.enCola > 0 && <> · {pesos(g.montoCola)}</>}
                {g.enCola > 0 && g.masAntiguo && (
                  <span className={minutos >= 60 ? 'tono-malo' : 'apagado'}>
                    {' '}
                    · el más antiguo lleva {minutos} min
                    {minutos >= 60 ? ' (revisa el plazo del SII)' : ''}
                  </span>
                )}
              </p>
              {g.enCola > 0 && (
                <button
                  type="button"
                  onClick={() => setCola(cola === g.grupo ? null : g.grupo)}
                  aria-expanded={cola === g.grupo}
                >
                  {cola === g.grupo ? 'Ocultar la cola' : 'Ver la cola'}
                </button>
              )}
              {!administra && (
                <p className="apagado">
                  Apagar o encender requiere el permiso «publicación al SII» (administrador).
                </p>
              )}
            </fieldset>
          );
        })}
      </div>
      {cola && <ColaPublicacion empresaId={empresaId} grupo={cola} alAbrirNota={alAbrirNota} />}
      {apagar && (
        <ApagarPublicacion
          grupo={apagar}
          ocupado={cambiar.isPending}
          alCerrar={() => setApagar(null)}
          alConfirmar={(motivo) =>
            cambiar.mutate(
              { grupo: apagar, activa: false, motivo },
              { onSuccess: () => setApagar(null) },
            )
          }
        />
      )}
    </div>
  );
}

function ColaPublicacion({
  empresaId,
  grupo,
  alAbrirNota,
}: {
  empresaId: string;
  grupo: Grupo;
  alAbrirNota: (notaId: string, titulo: string) => void;
}) {
  const qc = useQueryClient();
  const { puede } = useSesion();
  const q = useQuery({
    queryKey: ['emp', empresaId, 'dte-publicacion', 'cola', grupo],
    queryFn: () => api.get<EnCola[]>('/dte/publicacion/cola', { query: { empresaId, grupo } }),
    refetchInterval: 15_000,
  });
  // Filtros (Rodrigo, 2026-10-24): los errores se cometen en el punto de venta (venta local); lo que
  // sale a despacho casi nunca se corrige. Por eso la cola parte mostrando solo lo del local.
  const [origen, setOrigen] = useState<'LOCAL' | 'DESPACHO' | 'TODOS'>('LOCAL');
  const [pago, setPago] = useState<'TODOS' | 'INMEDIATO' | 'CREDITO'>('TODOS');
  const [texto, setTexto] = useState('');
  const [pasar, setPasar] = useState<EnCola | null>(null);
  const [anular, setAnular] = useState<EnCola | null>(null);
  const [imprimir, setImprimir] = useState<string | null>(null);
  const todos = q.data ?? [];
  const filas = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return todos.filter((d) => {
      const o = d.venta?.origen ?? 'LOCAL';
      if (origen !== 'TODOS' && o !== origen) return false;
      if (pago === 'CREDITO' && !d.venta?.credito) return false;
      if (pago === 'INMEDIATO' && d.venta?.credito) return false;
      if (
        t &&
        !`${d.folio ?? ''} ${d.receptor ?? ''} ${d.receptor_rut} ${d.venta?.notaNumero ?? ''}`
          .toLowerCase()
          .includes(t)
      )
        return false;
      return true;
    });
  }, [todos, origen, pago, texto]);
  const cuenta = (o: 'LOCAL' | 'DESPACHO') =>
    todos.filter((d) => (d.venta?.origen ?? 'LOCAL') === o).length;
  const total = filas.reduce((a, d) => a + Number(d.monto_total || 0), 0);
  const esBoleta = (d: EnCola) => d.tipo_dte === 39 || d.tipo_dte === 41;
  return (
    <fieldset className="recuadro-publicacion">
      <legend>En cola · {NOMBRE[grupo]}</legend>
      <div className="barra-filtros" role="group" aria-label="Filtros de la cola">
        <label className="campo">
          <span className="etiqueta">Origen</span>
          <select
            aria-label="Origen"
            value={origen}
            onChange={(e) => setOrigen(e.target.value as typeof origen)}
          >
            <option value="LOCAL">Punto de venta / local ({cuenta('LOCAL')})</option>
            <option value="DESPACHO">Despacho ({cuenta('DESPACHO')})</option>
            <option value="TODOS">Todos ({todos.length})</option>
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Condición de pago</span>
          <select
            aria-label="Condición de pago"
            value={pago}
            onChange={(e) => setPago(e.target.value as typeof pago)}
          >
            <option value="TODOS">Todas</option>
            <option value="INMEDIATO">
              Pago inmediato (contado, transferencia, cheque al día)
            </option>
            <option value="CREDITO">Crédito (o cheque a plazo)</option>
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar en la cola"
            placeholder="folio, cliente, RUT o N° de nota"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        <span className="apagado">
          {filas.length} de {todos.length} · {pesos(String(total))}
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label={`Cola de ${NOMBRE[grupo]}`}>
        <thead>
          <tr>
            <th>Documento</th>
            <th className="der">Folio</th>
            <th>Emisión</th>
            <th>Cliente</th>
            <th className="der">Monto</th>
            <th>Origen</th>
            <th>Pago</th>
            <th>Nota</th>
            <th>Timbrado</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((d) => (
            <tr key={d.id} data-origen={d.venta?.origen ?? 'LOCAL'}>
              <td>{TIPO[d.tipo_dte] ?? `Tipo ${d.tipo_dte}`}</td>
              <td className="der mono">{d.folio ?? '—'}</td>
              <td>{fecha(d.fecha_emision)}</td>
              <td>
                {d.receptor ?? '—'} <span className="apagado mono">{d.receptor_rut}</span>
              </td>
              <td className="der mono">{pesos(d.monto_total)}</td>
              <td>
                {d.venta
                  ? d.venta.origen === 'DESPACHO'
                    ? 'Despacho'
                    : (CANAL[d.venta.canal] ?? 'Local')
                  : '—'}
              </td>
              <td className={d.venta?.credito ? 'tono-aviso' : ''}>
                {d.venta ? (CONDICION[d.venta.condicionPago] ?? d.venta.condicionPago) : '—'}
              </td>
              <td className="mono">{d.venta?.notaNumero ?? '—'}</td>
              <td>{fechaHora(d.creado_en)}</td>
              <td className="acciones-fila">
                {esBoleta(d) &&
                  d.venta?.documentacionEstado === 'VIGENTE' &&
                  puede('ventas.vale.pasar_boleta') && (
                    <button
                      type="button"
                      className="boton-mini"
                      title="Anula la boleta antes del envío, la venta queda como vale con su pago y, si el cliente no se la llevó, la próxima boleta reutiliza el folio"
                      onClick={() => setPasar(d)}
                    >
                      Pasar a vale / liberar folio
                    </button>
                  )}
                {d.venta?.documentacionEstado === 'VIGENTE' &&
                  puede('ventas.venta.anular_duplicada') && (
                    <button
                      type="button"
                      className="boton-mini"
                      title="La misma venta se registró dos veces: anula el documento antes del envío, devuelve la mercadería, anula el recibo y la nota"
                      onClick={() => setAnular(d)}
                    >
                      Anular venta duplicada
                    </button>
                  )}
                <button
                  type="button"
                  className="enlace"
                  onClick={() =>
                    alAbrirNota(
                      d.venta?.notaId ?? d.origen_id,
                      `${TIPO[d.tipo_dte] ?? 'DTE'} N° ${d.folio ?? ''}`,
                    )
                  }
                >
                  Abrir para corregir
                </button>
              </td>
            </tr>
          ))}
          {q.data && !filas.length && (
            <tr>
              <td colSpan={10}>
                {todos.length ? 'Nada en cola con estos filtros.' : 'Nada en cola.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {pasar?.venta && (
        <PasarAVale
          documentacionId={pasar.venta.documentacionId}
          folio={pasar.folio}
          alCerrar={() => setPasar(null)}
          alListo={async (valeId) => {
            setPasar(null);
            await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'dte-publicacion'] });
            if (valeId) setImprimir(valeId);
          }}
        />
      )}
      {anular?.venta && (
        <AnularVentaDuplicada
          notaId={anular.venta.notaId}
          documento={`${TIPO[anular.tipo_dte] ?? 'Documento'} N° ${anular.folio ?? ''} · NV ${anular.venta.notaNumero}`}
          alCerrar={() => setAnular(null)}
          alListo={async () => {
            setAnular(null);
            await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'dte-publicacion'] });
          }}
        />
      )}
      {imprimir && <ImprimirVale valeId={imprimir} alTerminar={() => setImprimir(null)} />}
    </fieldset>
  );
}

function ApagarPublicacion({
  grupo,
  ocupado,
  alCerrar,
  alConfirmar,
}: {
  grupo: Grupo;
  ocupado: boolean;
  alCerrar: () => void;
  alConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const valido = motivo.trim().length >= 3;
  return (
    <Modal
      titulo={`Apagar la publicación de ${NOMBRE[grupo].toLowerCase()}`}
      alCerrar={alCerrar}
      pie={
        <div className="acciones">
          <button type="button" onClick={alCerrar}>
            Cancelar (Esc)
          </button>
          <button
            type="button"
            className="peligro"
            disabled={!valido || ocupado}
            onClick={() => alConfirmar(motivo.trim())}
          >
            Apagar
          </button>
        </div>
      }
    >
      <p>
        Se sigue vendiendo y emitiendo, pero {NOMBRE[grupo].toLowerCase()} quedarán en cola sin
        enviarse al SII hasta que la enciendas. Recuerda que el SII tiene plazo para recibirlas.
      </p>
      <label className="campo">
        <span className="etiqueta">Motivo (obligatorio)</span>
        <input
          aria-label="Motivo"
          data-autofoco
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && valido) alConfirmar(motivo.trim());
          }}
        />
      </label>
    </Modal>
  );
}
